import {useCallback} from 'react';
import {ResizeCallbackData} from 'react-resizable';

import {getRowsPerPageWithHeight} from './resizeUtils';
import {useWorkspaceSectionLayoutConfigs} from './WorkspaceSectionLayoutConfigs';

export const useResizableWorkspaceSection = ({
  panelsPerPage,
  setResizingSectionHeight,
  setRowsPerPage,
}: {
  panelsPerPage: number;
  setResizingSectionHeight: (height: number | null) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
}) => {
  const {
    columnsPerPage,
    currentPage,
    initialRowsPerPage,
    maxRowsInSection,
    panelHeight,
    setCurrentPage,
    updateFlowConfig,
  } = useWorkspaceSectionLayoutConfigs();

  const handleOnResize = useCallback(
    (_, data: ResizeCallbackData) => {
      const sectionHeight = data.size.height;
      const newRowsPerPage = getRowsPerPageWithHeight({
        maxRowsInSection,
        panelHeight,
        sectionHeight,
      });

      setResizingSectionHeight(sectionHeight);
      setRowsPerPage(newRowsPerPage);
    },
    [maxRowsInSection, panelHeight, setRowsPerPage, setResizingSectionHeight]
  );

  const handleResizeStop = useCallback(
    (_, data: ResizeCallbackData) => {
      const sectionHeight = data.size.height;

      setResizingSectionHeight(null);

      const newRowsPerPage = getRowsPerPageWithHeight({
        maxRowsInSection,
        panelHeight,
        sectionHeight,
      });

      // Don't update configs if the number of rows hasn't changed.
      if (newRowsPerPage === initialRowsPerPage) {
        return;
      }

      const firstVisiblePanelIndex = panelsPerPage * currentPage;
      const newCurrentPage = Math.floor(
        firstVisiblePanelIndex / (newRowsPerPage * columnsPerPage)
      );

      setCurrentPage(newCurrentPage);
      setRowsPerPage(newRowsPerPage);
      updateFlowConfig({
        rowsPerPage: newRowsPerPage,
      });
    },
    [
      setResizingSectionHeight,
      maxRowsInSection,
      panelHeight,
      initialRowsPerPage,
      panelsPerPage,
      currentPage,
      columnsPerPage,
      setCurrentPage,
      setRowsPerPage,
      updateFlowConfig,
    ]
  );

  return {
    handleOnResize,
    handleResizeStop,
  };
};
